import {
  pantoneCBook,
  pantoneCBookWithNeonColorsOnly,
  pantoneCBookWithNeonAndMetallicColors,
  pantoneCBookWithMetallicColorsOnly,
} from './PantoneColorBooks/PantoneCBook'
import { IUnifiedPickerTabConfig } from '../unifiedColorPicker/unifiedColorPicker.types'
import { TColorOption } from '../briefingForm/briefingFormProductColors.config'
import {
  blackSatinRibbonsColorOptionsCategories,
  whiteSatinRibbonsColorOptionsCategories,
} from '../briefingForm/preselectedColroOption'

export type TColorType = 'hex' | 'pantone'

export interface IOnlineEditorColorOptionConfig {
  pickerLabel: string
  pickerConfig: IUnifiedPickerTabConfig[]
  key: string
  defaultValue?: string
  colorType: TColorType
}

export type IOnlineEditorProductOptionsColorConfig = Record<
  TColorOption | string,
  IOnlineEditorColorOptionConfig[]
>
/** Picker tabs config */

/** Picker tabs config */
// Picker tab with regular Pantone C Book color list
const pantoneCBookPickerTabsConfig: IUnifiedPickerTabConfig[] = [
  {
    tabLabel: 'List',
    pickerType: 'list',
    colorsList: pantoneCBook,
    showSearchBox: true,
  },
]

// Picker tab with Pantone C Book + Neon + Metallic Colors list
const pantoneMetallicAndNeonPickerTabsConfig: IUnifiedPickerTabConfig[] = [
  {
    tabLabel: 'List',
    pickerType: 'list',
    colorsList: pantoneCBookWithNeonAndMetallicColors,
    showSearchBox: true,
  },
]

// Picker tab with Pantone C Book + Neon Colors list
const pantoneCbookAndNeonPickerTabsConfig: IUnifiedPickerTabConfig[] = [
  {
    tabLabel: 'List',
    pickerType: 'list',
    colorsList: pantoneCBookWithNeonColorsOnly,
    showSearchBox: true,
  },
]

// Picker tab with Pantone C Book + Metallic Colors list
const pantoneCbookAndMetallicPickerTabsConfig: IUnifiedPickerTabConfig[] = [
  {
    tabLabel: 'List',
    pickerType: 'list',
    colorsList: pantoneCBookWithMetallicColorsOnly,
    showSearchBox: true,
  },
]

// Picker tab for the hex color picker
const hexColorPickerTabConfig: IUnifiedPickerTabConfig[] = [
  {
    tabLabel: 'Standard',
    pickerType: 'hex',
  },
]

/** Color options config */
// Color options with Pantonce C Book configuration
export const logoOptionPantoneColorConfig: IOnlineEditorColorOptionConfig = {
  pickerLabel: 'Logo Color',
  pickerConfig: pantoneCBookPickerTabsConfig,
  key: 'logo-color',
  colorType: 'pantone',
}

const backgroundOptionPantoneColorConfig: IOnlineEditorColorOptionConfig = {
  pickerLabel: 'Background Color',
  pickerConfig: pantoneCBookPickerTabsConfig,
  key: 'background-color',
  colorType: 'pantone',
}

// Color options with Pantone C Book + Neon + Metallic configuration
const logoOptionNeonAndMetallicColorConfig: IOnlineEditorColorOptionConfig = {
  pickerLabel: 'Logo Color',
  pickerConfig: pantoneMetallicAndNeonPickerTabsConfig,
  key: 'logo-color',
  colorType: 'pantone',
}

const backgroundPantoneNeonColorConfig: IOnlineEditorColorOptionConfig = {
  pickerLabel: 'Background Color',
  pickerConfig: pantoneCbookAndNeonPickerTabsConfig,
  key: 'background-color',
  colorType: 'pantone',
}

// Color options with Pantone C Book + Metallic configuration
const logoOptionPantoneMetallicColorConfig: IOnlineEditorColorOptionConfig = {
  pickerLabel: 'Logo Color',
  pickerConfig: pantoneCbookAndMetallicPickerTabsConfig,
  key: 'logo-color',
  colorType: 'pantone',
}

const backgroundPantonMetallicColorConfig: IOnlineEditorColorOptionConfig = {
  pickerLabel: 'Background Color',
  pickerConfig: pantoneCbookAndMetallicPickerTabsConfig,
  key: 'background-color',
  colorType: 'pantone',
}

// Hex color options
const logoOptionHexColorConfig: IOnlineEditorColorOptionConfig = {
  pickerLabel: 'Logo Color',
  pickerConfig: hexColorPickerTabConfig,
  key: 'logo-color',
  colorType: 'hex',
}

const backgroundOptionHexColorConfig: IOnlineEditorColorOptionConfig = {
  pickerLabel: 'Background Color',
  pickerConfig: hexColorPickerTabConfig,
  key: 'background-color',
  colorType: 'hex',
}

// Satin Ribbon Color Presets
const whiteSatinRibbonColorConfig: IOnlineEditorColorOptionConfig = {
  pickerLabel: 'Logo Color',
  pickerConfig: [
    {
      tabLabel: 'List',
      pickerType: 'list',
      colorsList: whiteSatinRibbonsColorOptionsCategories.reduce(
        (list, category) => {
          return [...list, ...category?.colors]
        },
        []
      ),
      showSearchBox: true,
    },
  ],
  key: 'logo-color',
  colorType: 'pantone',
}

const blackSatinRibbonColorConfig: IOnlineEditorColorOptionConfig = {
  pickerLabel: 'Logo Color',
  pickerConfig: [
    {
      tabLabel: 'List',
      pickerType: 'list',
      colorsList: blackSatinRibbonsColorOptionsCategories.reduce(
        (list, category) => {
          return [...list, ...category?.colors]
        },
        []
      ),
      showSearchBox: true,
    },
  ],
  key: 'logo-color',
  colorType: 'pantone',
}

/* Presets, that repeat across many products */
const foodSafeColorConfigPreset = {
  default: [logoOptionPantoneColorConfig],
  '1 Color': [logoOptionPantoneColorConfig],
  '2 Colors': [
    logoOptionPantoneColorConfig,
    backgroundOptionPantoneColorConfig,
  ],
  '1 Color (Full Background)': [backgroundOptionPantoneColorConfig],
  '1 Color Full': [backgroundOptionPantoneColorConfig],
}

const tissueColorConfigPreset = {
  default: [logoOptionNeonAndMetallicColorConfig],
  '1 Color': [logoOptionNeonAndMetallicColorConfig],
  '2 Colors': [
    logoOptionPantoneMetallicColorConfig,
    backgroundPantoneNeonColorConfig,
  ],
  '1 Color (Full Background)': [backgroundPantoneNeonColorConfig],
}

export const onlineEditorProductsColorsConfig: Record<
  string,
  IOnlineEditorProductOptionsColorConfig
> = {
  'custom-printed-tissue-paper': tissueColorConfigPreset,
  'custom-kraft-tissue-paper': tissueColorConfigPreset,
  'custom-coated-paper': foodSafeColorConfigPreset,
  'recycled-coated-paper': {
    default: [logoOptionPantoneColorConfig],
  },
  'custom-foodsafe-paper': foodSafeColorConfigPreset,
  'custom-sandwich-wrap-paper': foodSafeColorConfigPreset,
  'custom-deli-paper': foodSafeColorConfigPreset,
  'custom-food-basket-liners': foodSafeColorConfigPreset,
  'custom-packing-tape': {
    default: [logoOptionHexColorConfig, backgroundOptionHexColorConfig],
  },
  'custom-reinforced-gummed-packing-tape': {
    default: [logoOptionHexColorConfig, backgroundOptionHexColorConfig],
  },
  'noissue-custom-washi-tape': {
    default: [logoOptionHexColorConfig, backgroundOptionHexColorConfig],
  },
  'custom-masking-tape': {
    default: [logoOptionHexColorConfig, backgroundOptionHexColorConfig],
  },
  'custom-coated-labels': {
    default: [logoOptionHexColorConfig, backgroundOptionHexColorConfig],
  },
  'custom-sticker-rolls': {
    default: [logoOptionHexColorConfig, backgroundOptionHexColorConfig],
  },
  'custom-eco-friendly-stickers': {
    default: [logoOptionHexColorConfig, backgroundOptionHexColorConfig],
  },
  'custom-roll-labels': {
    default: [logoOptionHexColorConfig, backgroundOptionHexColorConfig],
  },
  'custom-candle-labels': {
    default: [logoOptionHexColorConfig, backgroundOptionHexColorConfig],
  },
  'custom-recycled-satin-ribbon': {
    default: [blackSatinRibbonColorConfig],
    Black: [blackSatinRibbonColorConfig],
    White: [whiteSatinRibbonColorConfig],
  },
  'custom-die-cut-sticker-roll': {
    default: [logoOptionHexColorConfig, backgroundOptionHexColorConfig],
  },
  'custom-poly-mailers': {
    default: [logoOptionHexColorConfig, backgroundOptionHexColorConfig],
    '1 Color': [logoOptionHexColorConfig],
    '2 Color': [logoOptionHexColorConfig, backgroundOptionHexColorConfig],
    '1 Color (Full Background)': [backgroundOptionHexColorConfig],
    'Multi Color (CMYK)': [],
    '3 Colors': [logoOptionHexColorConfig, backgroundOptionHexColorConfig],
  },

  'wholesale-custom-poly-mailers': {
    default: [logoOptionHexColorConfig, backgroundOptionHexColorConfig],
    '1 Color': [logoOptionHexColorConfig],
    '2 Color': [logoOptionHexColorConfig, backgroundOptionHexColorConfig],
    '1 Color (Full Background)': [backgroundOptionHexColorConfig],
    '3 Colors': [logoOptionHexColorConfig, backgroundOptionHexColorConfig],
    'Multi Color (CMYK)': [
      logoOptionHexColorConfig,
      backgroundOptionHexColorConfig,
    ],
  },

  'custom-kraft-mailers': {
    default: [logoOptionHexColorConfig, backgroundOptionHexColorConfig],
  },
  'custom-kraft-mailer': {
    default: [logoOptionHexColorConfig, backgroundOptionHexColorConfig],
  },

  'custom-recycled-mailers-with-handles': {
    '1 Color': [logoOptionHexColorConfig],
    '1 Color (Full Background)': [backgroundOptionHexColorConfig],
    'Multi Color (CMYK)': [
      logoOptionHexColorConfig,
      backgroundOptionHexColorConfig,
    ],
  },

  'custom-tote-bags': {
    default: [logoOptionHexColorConfig],
  },
  'custom-recycled-die-cut-handle-bags': {
    default: [logoOptionHexColorConfig],
    '1 Color': [logoOptionHexColorConfig],
    '1 Color (Full Background)': [backgroundOptionHexColorConfig],
    'Multi Color (CMYK)': [
      logoOptionHexColorConfig,
      backgroundOptionHexColorConfig,
    ],
  },
  'custom-canvas-tote-bags': {
    default: [logoOptionHexColorConfig],
  },
  'custom-drawstring-bags': {
    default: [logoOptionHexColorConfig],
  },
  'custom-paper-bags-with-handles': {
    default: [logoOptionHexColorConfig, backgroundOptionHexColorConfig],
  },
  'custom-food-delivery-bags': {
    default: [logoOptionHexColorConfig, backgroundOptionHexColorConfig],
  },
  'custom-sandwich-bags': {
    default: [logoOptionHexColorConfig, backgroundOptionHexColorConfig],
  },

  'custom-paper-shopping-bags': {
    default: [logoOptionHexColorConfig],
    '1 Color': [logoOptionHexColorConfig],
    '1 Color (Full Background)': [backgroundOptionHexColorConfig],
    'Multi Color (CMYK)': [
      logoOptionHexColorConfig,
      backgroundOptionHexColorConfig,
    ],
    '2 Color': [logoOptionHexColorConfig, backgroundOptionHexColorConfig],
  },
  'custom-takeout-bags-with-handles': {
    default: [logoOptionHexColorConfig],
    '1 Color': [logoOptionHexColorConfig],
    '1 Color (Full Background)': [backgroundOptionHexColorConfig],
    'Multi Color (CMYK)': [
      logoOptionHexColorConfig,
      backgroundOptionHexColorConfig,
    ],
    '2 Color': [logoOptionHexColorConfig, backgroundOptionHexColorConfig],
  },
  'wholesale-custom-paper-bags-with-handles': {
    default: [logoOptionHexColorConfig],
    '1 Color': [logoOptionHexColorConfig],
    '1 Color (Full Background)': [backgroundOptionHexColorConfig],
    'Multi Color (CMYK)': [
      logoOptionHexColorConfig,
      backgroundOptionHexColorConfig,
    ],
    '2 Color': [logoOptionHexColorConfig, backgroundOptionHexColorConfig],
  },
  'wholesale-custom-paper-bags-food-service': {
    default: [logoOptionHexColorConfig],
    '1 Color': [logoOptionHexColorConfig],
    '1 Color (Full Background)': [backgroundOptionHexColorConfig],
    'Multi Color (CMYK)': [
      logoOptionHexColorConfig,
      backgroundOptionHexColorConfig,
    ],
    '2 Color': [logoOptionHexColorConfig, backgroundOptionHexColorConfig],
  },

  'wholesale-custom-compostable-mailers': {
    default: [logoOptionPantoneColorConfig],
    '1 Color': [logoOptionPantoneColorConfig],
    '2 Colors': [
      logoOptionPantoneColorConfig,
      backgroundOptionPantoneColorConfig,
    ],
  },
  'custom-compostable-mailers': {
    default: [logoOptionPantoneColorConfig],
    '1 Color': [logoOptionPantoneColorConfig],
    '2 Colors': [
      logoOptionPantoneColorConfig,
      backgroundOptionPantoneColorConfig,
    ],
  },
  'wholesale-custom-flexible-kraft-mailers': {
    default: [logoOptionPantoneColorConfig],
    '1 Color': [logoOptionPantoneColorConfig],
    '2 Colors': [
      logoOptionPantoneColorConfig,
      backgroundOptionPantoneColorConfig,
    ],
  },
}
